import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Activity, Loader2, ArrowDown, ArrowUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const factorLabelKey = (key) => ({
  purchase_price: 'sensitivity.factorPurchasePrice',
  rent:           'sensitivity.factorRent',
  interest_rate:  'sensitivity.factorInterestRate',
  vacancy:        'sensitivity.factorVacancy',
  maintenance:    'sensitivity.factorMaintenance',
}[key] || key);

const fmtDollar = (n) => (n === null || n === undefined || Number.isNaN(n))
  ? '—'
  : `${n >= 0 ? '+' : '-'}$${Math.abs(Math.round(n)).toLocaleString()}`;

const bandStyle = (band) => ({
  high:   { bar: 'bg-violet-600 dark:bg-violet-400',       label: 'sensitivity.bandHigh',   dot: 'bg-violet-600 dark:bg-violet-400' },
  medium: { bar: 'bg-sky-500 dark:bg-sky-400',             label: 'sensitivity.bandMedium', dot: 'bg-sky-500 dark:bg-sky-400' },
  low:    { bar: 'bg-slate-400 dark:bg-slate-500',         label: 'sensitivity.bandLow',    dot: 'bg-slate-400 dark:bg-slate-500' },
}[band] || { bar: 'bg-slate-400', label: 'sensitivity.bandLow', dot: 'bg-slate-400' });

const FactorRow = ({ factor }) => {
  const { t } = useTranslation();
  const style = bandStyle(factor.band);
  const widthPct = Math.max(1.5, Math.round(factor.normalized * 100));
  const adverseIsPlus = factor.adverse_direction === '+';
  const AdverseIcon = adverseIsPlus ? ArrowUp : ArrowDown;
  const favorableIsPlus = factor.favorable_direction === '+';
  const FavorableIcon = favorableIsPlus ? ArrowUp : ArrowDown;
  const deltaLabel = factor.delta_unit === 'pct'
    ? `${factor.requested_delta}%`
    : `${factor.requested_delta}pp`;

  return (
    <div
      data-testid={`sens-row-${factor.key}`}
      className="py-3 border-b border-slate-100 dark:border-slate-800 last:border-b-0"
    >
      {/* header row: label + band badge + magnitude */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${style.dot}`} aria-hidden="true" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {t(factorLabelKey(factor.key))}
          </span>
          <span
            data-testid={`sens-band-${factor.key}`}
            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-semibold ${
              factor.band === 'high'
                ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
                : factor.band === 'medium'
                  ? 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            }`}
          >
            {t(style.label)}
          </span>
        </div>
        <span
          data-testid={`sens-impact-${factor.key}`}
          className="text-sm font-mono tabular-nums text-slate-900 dark:text-slate-100 font-semibold"
        >
          {t('sensitivity.impactLabel')}: {fmtDollar(factor.impact_per_month)}/mo
        </span>
      </div>

      {/* bar */}
      <div
        className="w-full h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden"
        role="progressbar"
        aria-valuenow={Math.round(factor.normalized * 100)}
        aria-valuemin="0" aria-valuemax="100"
        aria-label={`${factor.key} sensitivity ${Math.round(factor.normalized * 100)}%`}
      >
        <div
          data-testid={`sens-bar-${factor.key}`}
          className={`h-full ${style.bar} transition-all duration-300`}
          style={{ width: `${widthPct}%` }}
        />
      </div>

      {/* direction breakdown */}
      <div className="grid grid-cols-2 gap-2 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
        <div data-testid={`sens-adverse-${factor.key}`} className="flex items-center gap-1">
          <AdverseIcon className="w-3 h-3 text-red-500 dark:text-red-400" />
          <span>
            {adverseIsPlus ? '+' : '−'}{deltaLabel}{factor.plus_clamped || factor.minus_clamped ? '*' : ''}
            {' → '}
            <span className="text-red-600 dark:text-red-400 font-mono tabular-nums">
              {fmtDollar(adverseIsPlus ? factor.delta_plus_vs_baseline : factor.delta_minus_vs_baseline)}
            </span>
          </span>
        </div>
        <div data-testid={`sens-favorable-${factor.key}`} className="flex items-center gap-1">
          <FavorableIcon className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
          <span>
            {favorableIsPlus ? '+' : '−'}{deltaLabel}
            {' → '}
            <span className="text-emerald-600 dark:text-emerald-400 font-mono tabular-nums">
              {fmtDollar(favorableIsPlus ? factor.delta_plus_vs_baseline : factor.delta_minus_vs_baseline)}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const SensitivityBars = ({ scenario }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Cache keyed by structural hash of the FULL canonical scenario —
  // any calculation-relevant change invalidates it. UI-only state
  // (accordion, hover) does NOT.
  const cacheRef = useRef({ hash: null, payload: null });

  const scenarioHash = useMemo(() => {
    // Deterministic ordered serialisation.
    const keys = Object.keys(scenario || {}).sort();
    return JSON.stringify(keys.map((k) => [k, scenario[k]]));
  }, [scenario]);

  const cacheStale = data && cacheRef.current.hash !== scenarioHash;

  const run = useCallback(async () => {
    if (cacheRef.current.hash === scenarioHash && cacheRef.current.payload) {
      setData(cacheRef.current.payload);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/scenario/sensitivity`,
        { scenario },
        { withCredentials: true }
      );
      cacheRef.current = { hash: scenarioHash, payload: resp.data };
      setData(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('sensitivity.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }, [scenario, scenarioHash, t]);

  const isZeroImpact = data && data.max_abs_impact === 0;

  return (
    <section
      data-testid="sensitivity-section"
      role="region"
      aria-live="polite"
      aria-atomic="false"
      className="mt-8 bg-white dark:bg-[#131824] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-violet-500" />
            <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100">
              {t('sensitivity.title')}
            </h2>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
            {t('sensitivity.subtitle')}
          </p>
        </div>
        <Button
          data-testid={data ? 'sensitivity-recompute-btn' : 'sensitivity-run-btn'}
          onClick={run}
          disabled={loading}
          size="sm"
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          {loading ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />{t('sensitivity.computing')}</>)
                   : (data ? t('sensitivity.recompute') : t('sensitivity.compute'))}
        </Button>
      </div>

      {cacheStale && (
        <p
          data-testid="sensitivity-stale"
          role="status"
          aria-live="polite"
          className="text-[11px] text-amber-600 dark:text-amber-400 mb-3"
        >
          {t('sensitivity.stale')}
        </p>
      )}

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {data && (
        <>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">
            {t('sensitivity.relativeNote')}
          </p>

          {isZeroImpact && (
            <p data-testid="sensitivity-zero-impact" className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              {t('sensitivity.zeroImpact')}
            </p>
          )}

          <div className="divide-y divide-transparent">
            {data.factors.map((f) => <FactorRow key={f.key} factor={f} />)}
          </div>

          {data.factors.some((f) => f.plus_clamped || f.minus_clamped) && (
            <p data-testid="sensitivity-clamp-note" className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 italic">
              {t('sensitivity.clampNote')}
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default SensitivityBars;
